import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken } = response.data;
        localStorage.setItem('accessToken', accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (err) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Auth Services
export const authService = {
  login: (email, password, resellerId, captchaToken) =>
    api.post('/auth/login', { email, password, resellerId, captchaToken, portal: 'admin' }),

  logout: () => api.post('/auth/logout'),

  getMe: () => api.get('/auth/me'),

  refreshToken: (refreshToken) =>
    api.post('/auth/refresh', { refreshToken }),

  forgotPassword: (email) =>
    api.post('/auth/forgot-password', { email, portal: 'admin' }),

  resetPassword: (token, password) =>
    api.post('/auth/reset-password', { token, password }),

  updateCurrency: (code, symbol) =>
    api.patch('/auth/update-currency', { code, symbol }),

  changePassword: (currentPassword, newPassword) =>
    api.patch('/auth/change-password', { currentPassword, newPassword }),

  updateProfile: (data) =>
    api.patch('/auth/update-profile', data),
};

// Guide Services — reseller-authored "How to Use" articles shown to
// their merchants under the profile dropdown in the customer portal.
// Same tenancy + upload pipeline as News; adds `section` and
// `youtubeUrl` fields on top.
export const guideService = {
  list:         (params) => api.get('/guides', { params }),
  getOne:       (id) => api.get(`/guides/${id}`),
  create:       (data) => api.post('/guides', data),
  update:       (id, data) => api.patch(`/guides/${id}`, data),
  toggleActive: (id) => api.patch(`/guides/${id}/toggle-active`),
  remove:       (id) => api.delete(`/guides/${id}`),
  // Attachments reuse the news-file upload endpoint — both need
  // image/PDF, reseller-scoped storage. No new backend needed.
  uploadFile: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/upload/news-file', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// News Services — reseller-authored news broadcast to their merchants.
// Reads open to any authenticated user; writes are reseller-only.
export const newsService = {
  list:         (params) => api.get('/news', { params }),
  getOne:       (id) => api.get(`/news/${id}`),
  create:       (data) => api.post('/news', data),
  update:       (id, data) => api.patch(`/news/${id}`, data),
  toggleActive: (id) => api.patch(`/news/${id}/toggle-active`),
  remove:       (id) => api.delete(`/news/${id}`),
  uploadFile: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/upload/news-file', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Reseller Services
export const resellerService = {
  getAll: (params) => api.get('/resellers', { params }),
  getOne: (id) => api.get(`/resellers/${id}`),
  create: (data) => api.post('/resellers', data),
  update: (id, data) => api.patch(`/resellers/${id}`, data),
  delete: (id) => api.delete(`/resellers/${id}`),
  suspend: (id, reason) => api.patch(`/resellers/${id}/suspend`, { reason }),
};

// Customer Services
export const customerService = {
  getAll: (params) => api.get('/customers', { params }),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.patch(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  suspend: (id, reason) => api.patch(`/customers/${id}/suspend`, { reason }),
  seedTemplates: (id) => api.post(`/customers/${id}/seed-templates`),
  createUser: (id, data) => api.post(`/customers/${id}/create-user`, data),
};

// Branch Services
export const branchService = {
  getAll: (params) => api.get('/branches', { params }),
  getByReseller: (resellerId) => api.get('/branches', { params: { resellerId, limit: 100 } }),
  getByCustomer: (customerId) => api.get('/branches', { params: { customerId, limit: 100 } }),
  getOne: (id) => api.get(`/branches/${id}`),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.patch(`/branches/${id}`, data),
  delete: (id) => api.delete(`/branches/${id}`),
  resetManagerPassword: (id, newPassword) => api.patch(`/branches/${id}/reset-manager-password`, { newPassword }),
};

// Plan Services
export const planService = {
  getAll: (params) => api.get('/plans', { params }),
  getOne: (id) => api.get(`/plans/${id}`),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.patch(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
};

// Product Services
export const productService = {
  getAll: (params) => api.get('/products', { params }),
  getOne: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.patch(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  updateInventory: (id, quantity) =>
    api.patch(`/products/${id}/inventory`, { quantity }),
};

// Invoice Services
export const invoiceService = {
  getAll: (params) => api.get('/invoices', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  getDashboardStats: () => api.get('/invoices/dashboard/stats'),
};
